import AttachmentIcon from "@mui/icons-material/Attachment";
import CancelIcon from "@mui/icons-material/Cancel";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Badge,
  Box,
  FormHelperText,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import "react-phone-input-2/lib/style.css";
import { useLocation, useNavigate } from "react-router-dom";
import AppLoader from "../../../../components/AppLoader";
import GlobalButton from "../../../../components/CustomButton";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import UploadImageAvatar from "../../../../components/UploadImage";
import appcolors from "../../../../constants/Colors";
import { supplierSendCode } from "../../../../services/supplier/authenticationApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { regiterSupplierValidation } from "../../../../utils/Validation";
import { baseUrl } from "../../../../services";
import { getAddressDetailByPlaceId } from "../../../../services/common/CommonApiFn";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import RomanianPhoneInput from "../../../../components/romaniaPhoneInput";
import { t } from "i18next";

const RegistrationForm = () => {
  const theme = useTheme();
  const attachmentRef = useRef(null);
  const navigate = useNavigate();
  const { state } = useLocation();

  // manage states
  const [showPass, setShowPass] = useState(false);
  const [isLoading, setIsLoading] = useState();
  const [location, setLocation] = useState(null);
  const [inputValues, setInputValues] = useState({
    image: null,
    name: "",
    email: "",
    contact: "",
    location: "",
    source: "",
    password: "",
    license: null,
    cui: "",
  });

  const [inputError, setInputError] = useState({
    image: false,
    name: false,
    email: false,
    isEmailValid: false,
    contact: false,
    source: false,
    location: false,
    password: false,
    isPasswordStrong: false,
    license: false,
    cui: false,
    isCuiValid: false,
  });

  useEffect(() => {
    if (state?.inputValues) {
      setInputValues((pre) => ({
        ...pre,
        image: state?.inputValues.image,
        name: state?.inputValues.name,
        email: state?.inputValues.email,
        contact: state?.inputValues.contact,
        location: state?.inputValues.location,
        source: state?.inputValues.source,
        password: state?.inputValues.password,
        license: state?.inputValues.license,
        cui: state?.inputValues.cui,
      }));
      setLocation(state?.inputValues.supplierLocation);
    }
  }, [state]);

  const handleRegister = async () => {
    try {
      const validate = regiterSupplierValidation(inputValues, setInputError);

      if (validate) {
        return;
      }

      if (!location) return handleOpenSnackBar("error", t("locationRequired"));

      setIsLoading(true);

      const payload = {
        email: inputValues.email,
        purpose: "Registration",
        role: "Supplier",
        cui: inputValues.cui,
      };

      const response = await supplierSendCode(payload);

      if (response.data.status === "success") {
        navigate(`/register/supplier/verification`, {
          state: {
            inputValues: { ...inputValues, supplierLocation: location },
          },
        });
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeValues = (event) => {
    const { name, value } = event.target;
    setInputError((prevError) => ({
      ...prevError,
      [name]: false,
      isEmailValid: name === "email" ? false : inputError.isEmailValid,
      isPasswordStrong:
        name === "password" ? false : inputError.isPasswordStrong,
      isCuiValid: name === "cui" ? false : inputError.isCuiValid,
    }));
    setInputValues((pre) => ({ ...pre, [name]: value }));
  };

  const handlUploadAttachment = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((preValues) => ({
        ...preValues,
        license: response.data.url,
      }));
      setInputError((preError) => ({ ...preError, license: false }));
      attachmentRef.current.value = null;
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageChange = async (event) => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prevValues) => ({
        ...prevValues,
        image: response?.data?.url,
      }));
      setInputError((prevError) => ({
        ...prevError,
        image: false,
      }));
      handleOpenSnackBar("success", t("imageUploadSuccess"));
    } catch (error) {
      handleOpenSnackBar("error", t("imageUploadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlaceDetails = async (object) => {
    try {
      setIsLoading(true);
      const response = await getAddressDetailByPlaceId(object.value.place_id);

      if (response?.data?.status === "success") {
        const data = response.data.data;
        const lat = data.result.geometry.location.lat;
        const lng = data.result.geometry.location.lng;

        setLocation({
          address: data.result.formatted_address,
          lat,
          lng,
        });
      } else {
        setLocation(null);
        setInputError((err) => ({ ...err, location: false }));
        handleOpenSnackBar("error", t("placeDetailsUnavailable"));
      }
    } catch (error) {
      setInputError((err) => ({ ...err, location: false }));
      setLocation(null);
      handleOpenSnackBar("error", t("errorFetchingPlaceDetails"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box component={"form"} sx={{ mt: 3 }}>
      <AppLoader isVisible={isLoading} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack alignItems={"center"}>
            <UploadImageAvatar
              error={inputError.image}
              image={inputValues.image}
              handleImageChange={handleImageChange}
            />
            {inputError.image && (
              <FormHelperText sx={{ color: "custom.errorRed" }}>
                {t("imageRequired")}
              </FormHelperText>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Label
            labelText={`${t("businessName")}`}
            htmlFor={"business-name"}
            required
          />
          <TextInput
            id={"business-name"}
            required={true}
            inputError={inputError.name}
            helperText={inputError.name && t("businessNameRequired")}
            placeholder={`${t("enter")} ${t("businessName")}`}
            type={"text"}
            name="name"
            value={inputValues.name}
            onChange={handleChangeValues}
          />
        </Grid>

        <Grid item xs={12}>
          <Label
            labelText={`${t("cui")} (${t("startsWithRO")})`}
            htmlFor={"cui"}
            required
          />
          <TextInput
            id={"cui"}
            required={true}
            inputError={inputError.cui || inputError.isCuiValid}
            helperText={
              inputError.cui
                ? t("cuiRequired")
                : inputError.isCuiValid
                ? t("invalidCuiFormat")
                : null
            }
            placeholder={t("enterCui")}
            type={"text"}
            name="cui"
            value={inputValues.cui}
            onChange={handleChangeValues}
          />
        </Grid>

        <Grid item xs={12}>
          <Label
            labelText={`${t("business")} ${t("emailAddress")}`}
            htmlFor={"business_email"}
            required
          />
          <TextInput
            id={"business_email"}
            required={true}
            inputError={inputError.email || inputError.isEmailValid}
            helperText={
              inputError.email
                ? t("emailRequired")
                : inputError.isEmailValid
                ? t("invalidEmailFormat")
                : null
            }
            placeholder={`${t("enter")} ${t("business")} ${t("emailAddress")}`}
            type={"email"}
            name="email"
            value={inputValues.email}
            onChange={handleChangeValues}
          />
        </Grid>

        <Grid item xs={12}>
          <Label
            labelText={t("contactNumber")}
            htmlFor={"contact_number"}
            required
          />
          <RomanianPhoneInput
            inputError={inputError.contact}
            value={inputValues.contact}
            onChange={(value) => {
              setInputValues((prev) => ({ ...prev, contact: value }));
              setInputError((err) => ({ ...err, contact: false }));
            }}
          />
          {/* <TextInput
            id={"contact_number"}
            required={true}
            inputError={inputError.contact}
            helperText={inputError.contact && "Contact number is required"}
            placeholder={"Enter contact number"}
            type={"number"}
            name="contact"
            value={inputValues.contact}
            onChange={handleChangeValues}
          /> */}
        </Grid>

        <Grid item xs={12}>
          <Stack
            mb={"5px"}
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Label
              labelText={t("searchLocation")}
              htmlFor={"location"}
              required
              labelProps={{ marginBottom: 0 }}
            />
            <Stack direction={"row"}>
              <Label
                labelText={t("current")}
                htmlFor={"location"}
                labelProps={{ marginBottom: 0, fontSize: "0.775rem" }}
              />
              <LocationOnOutlinedIcon
                htmlColor={appcolors.black}
                sx={{ fontSize: "1.2rem" }}
              />
            </Stack>
          </Stack>

          <GooglePlacesAutocomplete
            apiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
            selectProps={{
              styles: {
                control: (provided, state) => ({
                  ...provided,
                  padding: "4px",
                  borderRadius: "6px",
                  borderColor: inputError.location
                    ? theme.palette.error.main
                    : theme.palette.primary.main,
                  boxShadow: state.isFocused
                    ? `0 0 0 1px ${theme.palette.primary.main}`
                    : "none",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                  },
                }),
              },
              value: location?.addresss,
              onChange: (e) => {
                setInputError((err) => ({ ...err, location: false }));
                setInputValues((prev) => ({ ...prev, location: e }));
                fetchPlaceDetails(e);
              },
            }}
          />

          {inputError.location && (
            <FormHelperText sx={{ color: "#d32f2f" }}>
              {t("selectYourLocation")}
            </FormHelperText>
          )}

          {location && (
            <>
              <Label
                labelText={t("selectedAddress")}
                htmlFor={"location"}
                labelProps={{ mt: 1 }}
              />
              <TextInput
                id={"location"}
                required={true}
                inputError={inputError.location}
                helperText={inputError.location && t("locationRequired")}
                placeholder={`${t("enter")} ${t("location")}`}
                type={"text"}
                value={inputValues.location?.label}
                readonly
              />
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6">{t("businessInformation")}</Typography>
        </Grid>
        <Grid item xs={12}>
          <Label
            labelText={t("productSource")}
            htmlFor={"product_source"}
            required
          />
          <TextInput
            inputError={inputError.source}
            helperText={inputError.source && t("productSourceRequired")}
            id={"product_source"}
            required={true}
            placeholder={t("enterProductSource")}
            type={"text"}
            name="source"
            value={inputValues.source}
            onChange={handleChangeValues}
          />
        </Grid>

        <Grid item xs={12}>
          <Label labelText={t("password")} htmlFor={"password"} required />
          <TextInput
            inputError={inputError.password || inputError.isPasswordStrong}
            helperText={
              inputError.password
                ? t("passwordRequired")
                : inputError.isPasswordStrong
                ? t("passwordRequirements")
                : null
            }
            id={"password"}
            required
            placeholder={`${t("enter")} ${t("password")}`}
            type={showPass ? "text" : "password"}
            name="password"
            value={inputValues.password}
            onChange={handleChangeValues}
            InputEndIcon={
              showPass ? (
                <RemoveRedEyeIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowPass(false)}
                />
              ) : (
                <VisibilityOffIcon
                  sx={{ cursor: "pointer" }}
                  onClick={() => setShowPass(true)}
                />
              )
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Label
            labelText={t("attachBusinessLicense")}
            htmlFor={"location"}
            required
          />
          <Stack direction={"row"} alignItems={"center"} gap={2}>
            <Box>
              <input
                ref={attachmentRef}
                type="file"
                accept="image/*"
                onChange={handlUploadAttachment}
                style={{ display: "none" }}
              />
              <GlobalButton
                variant={"outlined"}
                btnText={"attach"}
                startIcon={<AttachmentIcon />}
                handleClickBtn={() => attachmentRef.current.click()}
                styleProps={{
                  width: "max-content",
                  height: 40,
                  borderWidth: 1,
                  borderRadius: "6px",
                  color: inputError.license
                    ? "custom.errorRed"
                    : "primary.main",
                  borderColor: inputError.license
                    ? "custom.errorRed"
                    : "primary.main",
                }}
                hoverProperties={{
                  borderWidth: 1,
                }}
              />
              {inputError.license && (
                <FormHelperText sx={{ color: "custom.errorRed" }}>
                  {t("attachmentRequired")}
                </FormHelperText>
              )}
            </Box>
            {inputValues.license && (
              <Badge
                sx={{
                  "& .MuiBadge-badge": {
                    top: "4%",
                    right: "4%",
                  },
                }}
                overlap="circular"
                badgeContent={
                  <CancelIcon
                    onClick={() => {
                      setInputValues((pre) => ({
                        ...inputValues,
                        license: null,
                      }));
                    }}
                    sx={{
                      fontSize: "1.125rem",
                      color: "custom.errorRed",
                      cursor: "pointer",
                    }}
                  />
                }
              >
                <Box
                  position={"relative"}
                  p={0.8}
                  border={"1px solid grey"}
                  borderRadius={2}
                  px={3}
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    window.open(inputValues.license, "_blank");
                  }}
                >
                  <Typography variant="body1">{t("view")}</Typography>
                </Box>
              </Badge>
            )}
          </Stack>
        </Grid>
      </Grid>
      <Box mt={4}>
        <GlobalButton
          variant={"contained"}
          btnText={"letsCreateAccount"}
          handleClickBtn={handleRegister}
        />
      </Box>
    </Box>
  );
};

export default RegistrationForm;
